import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { createLead } from "@/lib/api";
import { SERVICE_STATES, CONDITION_OPTIONS, TIMELINE_OPTIONS } from "@/constants/states";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const INITIAL = {
  full_name: "",
  phone: "",
  email: "",
  property_address: "",
  state: "",
  condition: "",
  timeline: "",
  notes: "",
};

export default function LeadForm({ variant = "section" }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.property_address.trim()) e.property_address = "Please enter the property address";
    if (!form.state) e.state = "Please select a state";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead({
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        property_address: form.property_address.trim(),
        state: form.state,
        condition: form.condition || undefined,
        timeline: form.timeline || undefined,
        notes: form.notes.trim() || undefined,
      });
      setSubmitted(true);
      toast.success("Got it! We'll be in touch within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="lead-form-success"
        className="bg-white rounded-3xl border border-[#E2DDD7] p-10 lg:p-14 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <div className="w-16 h-16 rounded-full bg-[#F1EDE7] flex items-center justify-center text-[#D95D39] mx-auto mb-6">
          <CheckCircle2 strokeWidth={1.5} className="w-8 h-8" />
        </div>
        <h3 className="font-[Outfit] font-semibold text-2xl sm:text-3xl text-[#2C3D30]">
          Thanks! Your information is in.
        </h3>
        <p className="mt-3 text-[#5B6B5F] max-w-md mx-auto">
          We&apos;ll review your property and reach out within 24 hours with a no-obligation cash offer.
        </p>
        <Button
          data-testid="lead-form-submit-another"
          onClick={() => setSubmitted(false)}
          className="mt-8 tr-btn-secondary rounded-full px-6 h-11"
        >
          Submit another property
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-form"
      onSubmit={onSubmit}
      className="bg-white rounded-3xl border border-[#E2DDD7] p-6 sm:p-10 lg:p-12 shadow-[0_20px_50px_-20px_rgba(44,61,48,0.18)]"
      noValidate
    >
      {variant === "section" && (
        <div className="mb-8">
          <span className="text-xs uppercase tracking-[0.2em] text-[#D95D39] font-medium">
            Get My Cash Offer
          </span>
          <h2 className="mt-3 font-[Outfit] font-semibold text-[#2C3D30] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Tell us about your home.
          </h2>
          <p className="mt-3 text-[#5B6B5F] text-sm sm:text-base">
            No fees. No obligation. We&apos;ll respond within 24 hours.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Your Name" id="full_name" error={errors.full_name}>
          <Input
            data-testid="form-input-name"
            id="full_name"
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            placeholder="Jane Smith"
            className="h-12 bg-white border-[#E2DDD7] focus-visible:ring-[#D95D39] rounded-xl"
          />
        </Field>

        <Field label="Phone" id="phone" error={errors.phone}>
          <Input
            data-testid="form-input-phone"
            id="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(555) 123-4567"
            className="h-12 bg-white border-[#E2DDD7] focus-visible:ring-[#D95D39] rounded-xl"
          />
        </Field>

        <Field label="Email" id="email" error={errors.email} className="sm:col-span-2">
          <Input
            data-testid="form-input-email"
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@example.com"
            className="h-12 bg-white border-[#E2DDD7] focus-visible:ring-[#D95D39] rounded-xl"
          />
        </Field>

        <Field label="Property Address" id="property_address" error={errors.property_address} className="sm:col-span-2">
          <Input
            data-testid="form-input-address"
            id="property_address"
            value={form.property_address}
            onChange={(e) => update("property_address", e.target.value)}
            placeholder="123 Main Street, Philadelphia"
            className="h-12 bg-white border-[#E2DDD7] focus-visible:ring-[#D95D39] rounded-xl"
          />
        </Field>

        <Field label="State" id="state" error={errors.state}>
          <Select value={form.state} onValueChange={(v) => update("state", v)}>
            <SelectTrigger
              data-testid="form-select-state"
              id="state"
              className="h-12 bg-white border-[#E2DDD7] focus:ring-[#D95D39] rounded-xl"
            >
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_STATES.map((s) => (
                <SelectItem
                  data-testid={`form-state-option-${s.code}`}
                  key={s.code}
                  value={s.code}
                >
                  {s.name} ({s.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Condition (optional)" id="condition">
          <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
            <SelectTrigger
              data-testid="form-select-condition"
              id="condition"
              className="h-12 bg-white border-[#E2DDD7] focus:ring-[#D95D39] rounded-xl"
            >
              <SelectValue placeholder="How's the house?" />
            </SelectTrigger>
            <SelectContent>
              {CONDITION_OPTIONS.map((c) => (
                <SelectItem key={c} value={c} data-testid={`form-condition-option-${c}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Timeline (optional)" id="timeline" className="sm:col-span-2">
          <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
            <SelectTrigger
              data-testid="form-select-timeline"
              id="timeline"
              className="h-12 bg-white border-[#E2DDD7] focus:ring-[#D95D39] rounded-xl"
            >
              <SelectValue placeholder="When would you like to sell?" />
            </SelectTrigger>
            <SelectContent>
              {TIMELINE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t} data-testid={`form-timeline-option-${t}`}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Anything else? (optional)" id="notes" className="sm:col-span-2">
          <Textarea
            data-testid="form-input-notes"
            id="notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Inherited, behind on payments, repairs needed, tenants — share whatever's relevant."
            rows={4}
            className="bg-white border-[#E2DDD7] focus-visible:ring-[#D95D39] rounded-xl resize-none"
          />
        </Field>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-[#5B6B5F]">
          By submitting, you agree to be contacted about your property. We never share your info.
        </p>
        <Button
          type="submit"
          data-testid="lead-form-submit"
          disabled={submitting}
          className="tr-btn-primary rounded-full h-14 px-10 font-medium text-base w-full sm:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            "Get My Cash Offer"
          )}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, id, error, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label htmlFor={id} className="text-sm font-medium text-[#2C3D30]">
        {label}
      </Label>
      {children}
      {error && (
        <span data-testid={`form-error-${id}`} className="text-xs text-[#D95D39]">
          {error}
        </span>
      )}
    </div>
  );
}
